/**
 * /customer-display?order_id=...
 *
 * Opened as a popup from the POS when the admin has "Customer Display" enabled.
 * Runs the Tip + Loyalty steps full-screen and posts the customer's choices
 * back to the POS window via BroadcastChannel("crest-customer-display").
 *
 * The card-entry step intentionally stays on the POS side — only the
 * customer-facing inputs (tip, loyalty phone) live here.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Crown, Phone, Spinner, PushPin } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const CHANNEL_NAME = "crest-customer-display";
const CFD_GEOM_KEY = "cfd_popup_geometry";
const CFD_PINNED_KEY = "cfd_display_pinned";

// Every Nth idle slide is the loyalty QR — so customers waiting at the
// counter always see "scan to join" within ~half a minute.
const QR_SLIDE_EVERY = 3;

// Floating pin/unpin control — lives in the top-right of the popup.
// Once the display is pinned, this hides itself so a curious customer or
// bad actor at the counter can't just click it and drag the window around.
// Unpinning requires a manager PIN.
const PinButton = ({ onPin, isPinned, onRequestUnpin }) => {
  if (isPinned) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="fixed top-3 right-3 z-50 gap-1.5 shadow-sm opacity-40 hover:opacity-100 transition-opacity"
        onClick={onRequestUnpin}
        data-testid="cd-unpin-display-btn"
        title="Pinned — click to unpin (manager PIN required)"
      >
        <PushPin size={14} weight="fill" />
        Unpin (mgr)
      </Button>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      className="fixed top-3 right-3 z-50 gap-1.5 shadow-sm"
      onClick={onPin}
      data-testid="cd-pin-display-btn"
      title="Save this window's position so every checkout opens here"
    >
      <PushPin size={14} weight="fill" />
      Pin to this display
    </Button>
  );
};

// Wrapper that renders BOTH the pin button and the manager-PIN unpin overlay
// so every render branch of the CFD only needs to drop in <PinOverlay ... />.
const PinOverlay = (props) => {
  const {
    onPin,
    isPinned,
    onRequestUnpin,
    showUnpinDialog,
    unpinPin,
    setUnpinPin,
    unpinBusy,
    onConfirmUnpin,
    onCancelUnpin,
  } = props;
  return (
    <>
      <PinButton onPin={onPin} isPinned={isPinned} onRequestUnpin={onRequestUnpin} />
      {showUnpinDialog && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="cd-unpin-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) onCancelUnpin(); }}
        >
          <div className="bg-card rounded-xl border border-border shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-1">Unpin customer display</h3>
            <p className="text-sm text-muted-foreground mb-4">
              A manager or admin PIN is required to unpin the display. This is here
              so a curious customer at the counter can&apos;t move the window around.
            </p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              value={unpinPin}
              onChange={(e) => setUnpinPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full h-14 rounded-md border border-border bg-background text-center text-3xl font-mono tracking-[14px]"
              placeholder="••••"
              data-testid="cd-unpin-pin-input"
              onKeyDown={(e) => { if (e.key === "Enter") onConfirmUnpin(); }}
            />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={onCancelUnpin} disabled={unpinBusy}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={onConfirmUnpin}
                disabled={unpinBusy || unpinPin.length !== 4}
                data-testid="cd-unpin-confirm"
              >
                {unpinBusy ? "Checking…" : "Confirm unpin"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CustomerDisplayPage = () => {
  const [params] = useSearchParams();
  const initialOrderId = params.get("order_id") || null;
  // Track the currently-active order id. null = idle mode (menu carousel).
  // Changes via BroadcastChannel "start_order" / "end_order" messages so
  // the popup can stay open all session and just swap views.
  const [orderId, setOrderId] = useState(initialOrderId);
  const [order, setOrder] = useState(null);
  const [posSettings, setPosSettings] = useState(null);
  const [step, setStep] = useState("tip"); // tip | loyalty | thanks
  const [error, setError] = useState(null);

  // Idle-mode menu carousel
  const [idleItems, setIdleItems] = useState([]);
  const [idleIndex, setIdleIndex] = useState(0);

  // Tip state
  const [tipAmount, setTipAmount] = useState(0);
  const [customTipInput, setCustomTipInput] = useState("");
  const [customTipOpen, setCustomTipOpen] = useState(false);

  // Loyalty state
  const [phone, setPhone] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [member, setMember] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollName, setEnrollName] = useState("");
  const [enrollEmail, setEnrollEmail] = useState("");
  const [redeemPoints, setRedeemPoints] = useState(0);

  // Reset checkout state when an order completes / new one begins.
  const resetCheckoutState = () => {
    setStep("tip");
    setTipAmount(0);
    setCustomTipInput("");
    setCustomTipOpen(false);
    setPhone("");
    setMember(null);
    setDeclined(false);
    setShowEnroll(false);
    setEnrollName("");
    setEnrollEmail("");
    setRedeemPoints(0);
  };

  // ------- POS settings + idle menu items (load once) -------
  useEffect(() => {
    axios.get(`${API}/pos-settings`)
      .then((s) => setPosSettings(s.data))
      .catch(() => setPosSettings({ tip_preset_1: 15, tip_preset_2: 18, tip_preset_3: 20 }));
    axios.get(`${API}/menu`)
      .then((m) => {
        const withImages = (m.data || []).filter((it) => it.image_url && it.available !== false);
        setIdleItems(withImages.slice(0, 12));
      })
      .catch(() => setIdleItems([]));
  }, []);

  // Rotate the idle carousel every 8s when in idle mode. The wrap covers
  // a full menu pass plus inserted QR cards, so the cycle is deterministic.
  useEffect(() => {
    if (orderId) return; // not idle
    if (idleItems.length < 1) return;
    const cycle = (QR_SLIDE_EVERY + 1) * Math.max(1, idleItems.length);
    const t = setInterval(() => setIdleIndex((i) => (i + 1) % cycle), 8000);
    return () => clearInterval(t);
  }, [orderId, idleItems.length]);

  // ------- Order details: load whenever orderId changes -------
  // The `ignore` flag prevents a slow fetch for a *previous* order from
  // clobbering state after the POS has already moved on (end_order arrived,
  // new start_order arrived, etc.) — the "constantly saying Couldn't load
  // order details" bug we've been chasing.
  useEffect(() => {
    if (!orderId) { setOrder(null); setError(null); resetCheckoutState(); return; }
    setError(null);
    let ignore = false;
    axios.get(`${API}/orders/${orderId}`)
      .then((o) => { if (!ignore) setOrder(o.data); })
      .catch(() => { if (!ignore) setError("Couldn't load order details. The POS may have closed."); });
    return () => { ignore = true; };
  }, [orderId]);

  // ------- BroadcastChannel: POS pushes start_order / end_order -------
  // Lets the popup stay open between checkouts (idle carousel by default)
  // and flip into checkout view instantly when a new order is rung up. No
  // window creation, no nav — sub-millisecond view swap.
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type === "start_order" && msg.orderId) {
        resetCheckoutState();
        setError(null);
        setOrderId(msg.orderId);
      } else if (msg.type === "end_order") {
        // Explicitly wipe the error banner alongside the order so a failed
        // previous checkout doesn't leave a stale "couldn't load" screen
        // showing between orders.
        setError(null);
        setOrder(null);
        setOrderId(null);
      } else if (msg.type === "cfd_ping") {
        // Aliveness check from the POS's auto-persist hook. Reply so the
        // POS doesn't spawn a duplicate popup.
        try { ch.postMessage({ type: "cfd_alive" }); } catch { /* ignore */ }
      }
    };
    return () => ch.close();
  }, []);

  // Persist the popup's final geometry the moment the cashier closes the
  // window. The POS-side 2s poll can miss the very last drag — beforeunload
  // is the only guaranteed last-chance hook to capture it.
  useEffect(() => {
    const save = () => {
      try {
        if (!Number.isFinite(window.screenX) || !Number.isFinite(window.screenY)) return;
        // Use opener.localStorage so the value lives in the POS origin too.
        const payload = JSON.stringify({
          left: window.screenX,
          top: window.screenY,
          width: window.outerWidth,
          height: window.outerHeight,
        });
        try { window.opener?.localStorage?.setItem(CFD_GEOM_KEY, payload); } catch { /* cross-origin */ }
        try { localStorage.setItem(CFD_GEOM_KEY, payload); } catch { /* quota */ }
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", save);
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("beforeunload", save);
      window.removeEventListener("pagehide", save);
    };
  }, []);

  const [isPinned, setIsPinned] = useState(() => {
    try { return localStorage.getItem(CFD_PINNED_KEY) === "1"; } catch { return false; }
  });
  const [showUnpinDialog, setShowUnpinDialog] = useState(false);
  const [unpinPin, setUnpinPin] = useState("");
  const [unpinBusy, setUnpinBusy] = useState(false);

  const pinToThisDisplay = () => {
    try {
      const payload = JSON.stringify({
        left: window.screenX,
        top: window.screenY,
        width: window.outerWidth,
        height: window.outerHeight,
      });
      try { window.opener?.localStorage?.setItem(CFD_GEOM_KEY, payload); } catch { /* cross-origin */ }
      localStorage.setItem(CFD_GEOM_KEY, payload);
      // Lock the pin — from here on, unpinning takes a manager PIN. Prevents
      // customers/bystanders from unpinning and moving the display around.
      localStorage.setItem(CFD_PINNED_KEY, "1");
      try { window.opener?.localStorage?.setItem(CFD_PINNED_KEY, "1"); } catch { /* cross-origin */ }
      setIsPinned(true);
      toast.success(`Pinned to this display (${window.screenX}, ${window.screenY}). Unpin requires a manager PIN.`);
    } catch (e) {
      toast.error("Couldn't pin position: " + (e?.message || "unknown"));
    }
  };

  const requestUnpin = () => {
    setUnpinPin("");
    setShowUnpinDialog(true);
  };

  const confirmUnpin = async () => {
    if (unpinPin.length < 4) return;
    setUnpinBusy(true);
    try {
      // Reuses the existing manager-pin verification endpoint that's already
      // used for voids/refunds/discounts. Any admin or manager PIN unlocks.
      // Endpoint returns { ok: true, manager_name, manager_id } on success —
      // it raises 4xx on failure so we don't get a "valid: false" body.
      const res = await axios.post(`${API}/auth/verify-manager-pin`, { pin: unpinPin });
      if (res.data?.ok) {
        localStorage.removeItem(CFD_PINNED_KEY);
        localStorage.removeItem(CFD_GEOM_KEY);
        try { window.opener?.localStorage?.removeItem(CFD_PINNED_KEY); } catch { /* cross-origin */ }
        try { window.opener?.localStorage?.removeItem(CFD_GEOM_KEY); } catch { /* cross-origin */ }
        setIsPinned(false);
        setShowUnpinDialog(false);
        toast.success("Unpinned. Next open will use default geometry.");
      } else {
        toast.error("That PIN doesn't belong to a manager or admin.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "PIN verification failed.");
    } finally {
      setUnpinBusy(false);
      setUnpinPin("");
    }
  };

  const subtotal = order?.subtotal || 0;
  const tax = order?.tax || 0;
  const discount = Math.min(redeemPoints / 100, subtotal);
  const adjusted = Math.max(0, subtotal - discount);
  const total = adjusted + tax + tipAmount;

  const tipPresets = posSettings ? [
    posSettings.tip_preset_1 || 15,
    posSettings.tip_preset_2 || 18,
    posSettings.tip_preset_3 || 20,
  ] : [];

  const postResultAndClose = (payload) => {
    try {
      const ch = new BroadcastChannel(CHANNEL_NAME);
      ch.postMessage({ type: "wizard_complete", orderId, payload });
      ch.close();
    } catch {
      // BroadcastChannel unsupported → fall back to opener postMessage.
      try { window.opener?.postMessage({ type: "wizard_complete", orderId, payload }, "*"); } catch { /* noop */ }
    }
    setStep("thanks");
    setTimeout(() => { try { window.close(); } catch { /* noop */ } }, 1800);
  };

  const handleLookupPhone = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) { toast.error("Enter a valid phone number"); return; }
    setLookupLoading(true);
    try {
      const r = await axios.get(`${API}/loyalty/lookup-phone/${encodeURIComponent(digits)}`);
      if (r.data.found) { setMember(r.data.customer); setShowEnroll(false); }
      else { setMember(null); setShowEnroll(true); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleEnroll = async () => {
    const digits = phone.replace(/\D/g, "");
    if (!enrollName || !enrollEmail || digits.length < 7) {
      toast.error("Name, email, and phone are required"); return;
    }
    try {
      const r = await axios.post(`${API}/loyalty/customers`, {
        name: enrollName, email: enrollEmail, phone: digits,
      });
      setMember(r.data); setShowEnroll(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrollment failed");
    }
  };

  const handleDecline = () => {
    setDeclined(true);
    setMember(null);
    setRedeemPoints(0);
    postResultAndClose({ tip_amount: tipAmount, loyalty_declined: true, redeem_points: 0 });
  };

  const handleComplete = () => {
    postResultAndClose({
      tip_amount: tipAmount,
      loyalty_member: member,
      loyalty_declined: declined,
      redeem_points: redeemPoints,
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <PinOverlay
        onPin={pinToThisDisplay}
        isPinned={isPinned}
        onRequestUnpin={requestUnpin}
        showUnpinDialog={showUnpinDialog}
        unpinPin={unpinPin}
        setUnpinPin={setUnpinPin}
        unpinBusy={unpinBusy}
        onConfirmUnpin={confirmUnpin}
        onCancelUnpin={() => setShowUnpinDialog(false)}
      />
        <div className="text-center max-w-md">
          <p className="text-2xl font-medium mb-2">Customer display</p>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // ----- IDLE MODE — menu carousel + loyalty QR -----
  if (!orderId) {
    // The carousel rotates through menu items, then inserts a QR card
    // after every QR_SLIDE_EVERY items. idleIndex bumps once per 8 s.
    const isQrSlot = idleItems.length === 0
      || (idleIndex > 0 && (idleIndex % (QR_SLIDE_EVERY + 1)) === QR_SLIDE_EVERY);
    const item = !isQrSlot && idleItems.length > 0
      ? idleItems[Math.floor(idleIndex - Math.floor(idleIndex / (QR_SLIDE_EVERY + 1))) % idleItems.length]
      : null;
    const signupUrl = `${window.location.origin}/loyalty-signup`;

    return (
      <div
        className="min-h-screen bg-background flex flex-col items-center justify-center p-10 relative overflow-hidden"
        data-testid="cd-idle-root"
      >
        <PinOverlay
        onPin={pinToThisDisplay}
        isPinned={isPinned}
        onRequestUnpin={requestUnpin}
        showUnpinDialog={showUnpinDialog}
        unpinPin={unpinPin}
        setUnpinPin={setUnpinPin}
        unpinBusy={unpinBusy}
        onConfirmUnpin={confirmUnpin}
        onCancelUnpin={() => setShowUnpinDialog(false)}
      />
        {isQrSlot || !item ? (
          <div
            key="qr-slide"
            className="w-full max-w-3xl rounded-3xl overflow-hidden shadow-xl bg-card p-8 flex flex-col sm:flex-row items-center gap-8"
            style={{ animation: "cd-fade-in 600ms ease-out" }}
            data-testid="cd-idle-qr"
          >
            <div className="shrink-0 bg-white p-4 rounded-2xl border border-border">
              <QRCodeSVG
                value={signupUrl}
                size={260}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#1f1f1f"
              />
            </div>
            <div className="flex-1 text-center sm:text-left space-y-3">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <Crown size={32} weight="fill" className="text-primary" />
                <h2 className="font-heading text-4xl">Join our rewards</h2>
              </div>
              <p className="text-muted-foreground text-lg leading-snug">
                Scan with your phone camera to enroll in seconds.
              </p>
              <ul className="text-base space-y-1.5 text-muted-foreground">
                <li>• Earn <span className="text-foreground font-medium">1 point per $1</span> spent</li>
                <li>• Free perks at Silver, Gold and Platinum tiers</li>
                <li>• No app to download</li>
              </ul>
            </div>
          </div>
        ) : (
          <div
            key={item.id}
            className="w-full max-w-3xl rounded-3xl overflow-hidden shadow-xl"
            style={{ animation: "cd-fade-in 600ms ease-out" }}
          >
            <img
              src={item.image_url}
              alt={item.name}
              className="w-full h-[55vh] object-cover"
              loading="eager"
            />
            <div className="bg-card p-6 flex items-end justify-between gap-4">
              <div>
                <h2 className="font-heading text-4xl mb-1">{item.name}</h2>
                {item.description && (
                  <p className="text-muted-foreground text-base line-clamp-2 max-w-xl">{item.description}</p>
                )}
              </div>
              <p className="font-mono text-3xl font-semibold text-primary">${Number(item.price).toFixed(2)}</p>
            </div>
          </div>
        )}
        <p className="mt-8 text-sm text-muted-foreground">Approach the counter when you&apos;re ready to order</p>
        <style>{`@keyframes cd-fade-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }`}</style>
      </div>
    );
  }

  if (!order || !posSettings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <PinOverlay
        onPin={pinToThisDisplay}
        isPinned={isPinned}
        onRequestUnpin={requestUnpin}
        showUnpinDialog={showUnpinDialog}
        unpinPin={unpinPin}
        setUnpinPin={setUnpinPin}
        unpinBusy={unpinBusy}
        onConfirmUnpin={confirmUnpin}
        onCancelUnpin={() => setShowUnpinDialog(false)}
      />
        <Spinner size={48} className="animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-6 flex items-start justify-center" data-testid="customer-display-root">
      <PinOverlay
        onPin={pinToThisDisplay}
        isPinned={isPinned}
        onRequestUnpin={requestUnpin}
        showUnpinDialog={showUnpinDialog}
        unpinPin={unpinPin}
        setUnpinPin={setUnpinPin}
        unpinBusy={unpinBusy}
        onConfirmUnpin={confirmUnpin}
        onCancelUnpin={() => setShowUnpinDialog(false)}
      />
      <div className="w-full max-w-2xl space-y-6">
        <header>
          <h1 className="font-heading text-4xl mb-1">Welcome</h1>
          <p className="text-muted-foreground">Order #{order.id.slice(-6)} · Table {order.table_name}</p>
        </header>

        <div className="rounded-2xl border border-border bg-card p-5 space-y-2 font-mono" data-testid="cd-summary">
          <div className="flex justify-between"><span className="font-sans">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          {discount > 0 && (
            <div className="flex justify-between text-accent"><span className="font-sans">Loyalty discount</span><span>−${discount.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between"><span className="font-sans">Tax</span><span>${tax.toFixed(2)}</span></div>
          {tipAmount > 0 && <div className="flex justify-between text-accent"><span className="font-sans">Tip</span><span>${tipAmount.toFixed(2)}</span></div>}
          <Separator className="my-2" />
          <div className="flex justify-between text-2xl font-semibold"><span className="font-sans">Total</span><span>${total.toFixed(2)}</span></div>
        </div>

        {step === "tip" && (
          <section className="space-y-4" data-testid="cd-tip-step">
            <h2 className="font-heading text-2xl">Add a tip?</h2>
            <div className="grid grid-cols-3 gap-3">
              {tipPresets.map((pct, i) => (
                <Button
                  key={`${pct}-${i}`}
                  variant={tipAmount === parseFloat((subtotal * pct / 100).toFixed(2)) ? "default" : "outline"}
                  className="h-24 flex-col text-lg"
                  onClick={() => setTipAmount(parseFloat((subtotal * pct / 100).toFixed(2)))}
                  data-testid={`cd-tip-preset-${i + 1}`}
                >
                  <span className="text-3xl font-semibold">{pct}%</span>
                  <span className="font-mono opacity-75 text-sm">${(subtotal * pct / 100).toFixed(2)}</span>
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={customTipOpen ? "default" : "outline"}
                className="h-16 text-base"
                onClick={() => setCustomTipOpen(true)}
                data-testid="cd-tip-custom"
              >
                Custom amount
              </Button>
              <Button
                variant={tipAmount === 0 ? "default" : "outline"}
                className="h-16 text-base"
                onClick={() => setTipAmount(0)}
                data-testid="cd-tip-none"
              >
                No tip
              </Button>
            </div>
            {customTipOpen && (
              <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-2">
                <Label>Enter custom tip ($)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={customTipInput}
                    onChange={(e) => setCustomTipInput(e.target.value)}
                    className="font-mono text-lg h-12"
                    autoFocus
                    data-testid="cd-tip-custom-input"
                  />
                  <Button
                    className="h-12"
                    onClick={() => {
                      const v = parseFloat(customTipInput);
                      if (Number.isFinite(v) && v >= 0) {
                        setTipAmount(parseFloat(v.toFixed(2))); setCustomTipOpen(false); setCustomTipInput("");
                      } else toast.error("Enter a valid amount");
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button className="h-14 px-8 text-lg" onClick={() => setStep("loyalty")} data-testid="cd-next-loyalty">
                Continue
              </Button>
            </div>
          </section>
        )}

        {step === "loyalty" && (
          <section className="space-y-4" data-testid="cd-loyalty-step">
            <h2 className="font-heading text-2xl flex items-center gap-2">
              <Crown size={28} weight="fill" className="text-accent" />
              Loyalty rewards
            </h2>

            {!member && !showEnroll && (
              <div className="space-y-3">
                <p className="text-muted-foreground">Enter your phone number to earn or redeem points, or skip to finish.</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="text-lg h-14"
                    data-testid="cd-phone-input"
                  />
                  <Button
                    onClick={handleLookupPhone}
                    disabled={lookupLoading || phone.replace(/\D/g, "").length < 7}
                    className="h-14 px-6 gap-2"
                    data-testid="cd-lookup-btn"
                  >
                    {lookupLoading ? <Spinner size={18} className="animate-spin" /> : <Phone size={18} />}
                    Look up
                  </Button>
                </div>
              </div>
            )}

            {showEnroll && !member && (
              <div className="rounded-lg border border-border p-4 space-y-3" data-testid="cd-enroll-form">
                <p>We didn&apos;t find an account. Want to join?</p>
                <div className="space-y-1"><Label>Full name</Label>
                  <Input value={enrollName} onChange={(e) => setEnrollName(e.target.value)} className="h-12" data-testid="cd-enroll-name" />
                </div>
                <div className="space-y-1"><Label>Email</Label>
                  <Input type="email" value={enrollEmail} onChange={(e) => setEnrollEmail(e.target.value)} className="h-12" data-testid="cd-enroll-email" />
                </div>
                <div className="flex justify-between gap-2 pt-1">
                  <Button variant="ghost" onClick={() => { setShowEnroll(false); }}>Back</Button>
                  <Button onClick={handleEnroll} data-testid="cd-enroll-submit">Enroll &amp; continue</Button>
                </div>
              </div>
            )}

            {member && (
              <CDLoyaltyMember member={member} subtotal={subtotal} redeemPoints={redeemPoints} onRedeemChange={setRedeemPoints} />
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" className="h-14 px-6 text-base" onClick={handleDecline} data-testid="cd-decline-btn">
                Decline &amp; finish
              </Button>
              <Button
                className="h-14 px-8 text-lg"
                onClick={handleComplete}
                disabled={!member && !declined && !showEnroll}
                data-testid="cd-finish-btn"
              >
                Finish &amp; hand back
              </Button>
            </div>
          </section>
        )}

        {step === "thanks" && (
          <section className="py-16 text-center space-y-4" data-testid="cd-thanks-step">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={60} weight="fill" className="text-green-600" />
            </div>
            <p className="text-2xl font-medium">Thank you!</p>
            <p className="text-muted-foreground">Please hand the screen back to the cashier.</p>
          </section>
        )}
      </div>
    </div>
  );
};

const CDLoyaltyMember = ({ member, subtotal, redeemPoints, onRedeemChange }) => {
  const maxRedeemable = Math.min(member.points, Math.floor(subtotal * 100));
  const tierColors = { bronze: "text-amber-700", silver: "text-slate-500", gold: "text-yellow-600", platinum: "text-purple-600" };
  const presets = useMemo(() => {
    const opts = [];
    if (member.points >= 100) opts.push(100);
    if (member.points >= 500) opts.push(500);
    if (member.points >= 1000) opts.push(1000);
    if (member.points >= 100 && member.points <= maxRedeemable) opts.push(member.points);
    return Array.from(new Set(opts)).filter(p => p <= maxRedeemable).slice(0, 4);
  }, [member.points, maxRedeemable]);

  return (
    <div className="rounded-2xl border border-border p-5 space-y-3" data-testid="cd-member-panel">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Welcome back</div>
          <div className="text-2xl font-medium">{member.name}</div>
        </div>
        <div className={`text-base font-semibold uppercase ${tierColors[member.tier] || ""}`}>{member.tier}</div>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="text-4xl font-mono font-semibold" data-testid="cd-points">{member.points.toLocaleString()}</div>
        <div className="text-sm text-muted-foreground">points available</div>
      </div>

      <div>
        <div className="text-sm text-muted-foreground mb-2">100 points = $1.00 off</div>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <Button variant={redeemPoints === 0 ? "default" : "outline"} onClick={() => onRedeemChange(0)} data-testid="cd-redeem-none">None</Button>
          {presets.map((p) => (
            <Button
              key={p}
              variant={redeemPoints === p ? "default" : "outline"}
              onClick={() => onRedeemChange(p)}
              className="flex-col h-auto py-1"
              data-testid={`cd-redeem-${p}`}
            >
              <span className="text-base">{p}</span>
              <span className="opacity-75 font-mono text-xs">−${(p / 100).toFixed(2)}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomerDisplayPage;
